import "./SideBar.scss"
import "../../colorHelpers.scss"
import "assets/styles/commons.scss"
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react"
import axios, { CancelTokenSource } from "axios"
import {
  handleTreeViewCheckChange,
  processTreeViewItems,
  TreeView,
  TreeViewCheckChangeEvent,
  TreeViewCheckDescriptor,
  TreeViewExpandChangeEvent,
} from "@progress/kendo-react-treeview"

import { workspaceSlice } from "store/workspace/slice"
import { useAppDispatch, useAppSelector } from "../../hooks/redux"
import {  T_editWorkspaceFilters, T_editWorkspaceFilterOptions, saveMapOptionsThunk } from "store/workspace/thunk"
import {
  getCategory1TagAttributesValuesThunk,
  getNumericTagAttributesValuesThunk,
  getProductionTagAttributesValuesThunk,
  getQuickPlotsThunk,
  getSideBarFiltersThunk,
  T_getProductionPlotIndentifierOptions,
  TH_GetAllTypes,
} from "store/sidebar/thunk"
import { T_getProductionPlotData } from "store/explore/thunk"
import { combineClassNames, deepClone } from "helpers/functions/commons"
import { TreeViewDataItem } from "store/sidebar/types"
import { useLocation } from "react-router-dom"
import { selectAllTagAttributes } from "store/sidebar/selectors"
import { getMapViewFilteredPointsThunk } from "store/mapView/thunk"
import usePrevious from "hooks/usePrevious"
import { PlotOptions } from "components/PlotOptions/PlotOptions"
import { HistogramOptions } from "components/HistogramOptions/HistogramOptions"
import { MapOptions } from "components/mapOptions/MapOptions"
import { ROUTES, SCREEN_RESOLUTIONS } from "helpers/constants/app"
import { IWorkspaceItem } from "store/workspace/types"
import { isReadOnlyAccess } from "helpers/functions/workspaceHelpers"
import { ProductionPlotOptions } from "components/Charts/productionPlotOptions/productionPlotOptions"
import { ProjectViewOptions } from "components/ProjectViewOptions/ProjectViewOptions"
import { FilterOptions } from "components/FilterOptions/FilterOptions"
import { BenchmarkOptions } from "components/benckmarkOptions/benchmarkOptions"
import { GridOptions } from "components/GridOptions/GridOptions"
import { sideBarTreeSlice } from "store/sidebar/slice"
import { T_getDatasetsList } from "store/userData/thunk"
import TreeItem from "./TreeItem"
import { requestDeduplicationService } from "helpers/functions/requestDeduplication"
import { IElementType } from "components/SideBarItems/types"

const SideBar = () => {
  const dispatch = useAppDispatch()
  const { filtersTreeView, tree, tabs, workspaceId, workspaces, filteredFilters, filters, plotId, mapOptions } = useAppSelector((state) => state.workspaceSlice)
  const { isFetched: isFetchedMap } = useAppSelector((state) => state.mapViewSlice)
  const { filterMode } = useAppSelector((state) => state.analogueSlice);
  const { licenseInfo } = useAppSelector((state) => state.userStateSlice)
  const tagAttributes = useAppSelector(selectAllTagAttributes)
  const propertyCalculatorParameters = useAppSelector((state) => state.sideBarTreeSlice.propertyCalculatorParameters)
  const { entityId } = useAppSelector((store) => store.workspaceSlice.userActions)
  const isReadOnly = isReadOnlyAccess(workspaces.find((w: IWorkspaceItem) => w.id === workspaceId) || null)
  const { pathname } = useLocation()
  const filtersSearchText = useAppSelector((state) => state.sideBarTreeSlice.filtersSearchText)
  const isSidebarOpen = useAppSelector((state) => state.sideBarTreeSlice.isSidebarOpen)
  const productionPlotSeries = useAppSelector((state) => state.sideBarTreeSlice.productionPlot?.productionPlotOptions?.productionPlotSeries)
  const [check, setCheck] = useState<string[] | TreeViewCheckDescriptor>([])
  const [expand, setExpand] = useState<{ ids: string[]; idField: string }>({ ids: [""], idField: "" })
  const [filtersOpened, setFiltersOpened] = useState<boolean>(true)

  // const [isOpened, setIsOpened] = useState(true);

  const initialized = useRef(false)
  const initializedEntity = useRef(false)
  const initialWorkspaceUnit = useRef<number | null>(null)
  const mapEndpointSource = useRef<CancelTokenSource | null>(null)
  const selectedTab = tabs?.find((item) => item.isSelected)
  const location = useLocation()


  const handleExpandChange = (item: TreeViewDataItem) => {
    if ((filteredFilters.length && item.type === "title") || !tree) return
    const clonedTree = deepClone(tree)
    const ids = expand.ids.slice()
    const index = ids.indexOf(item.id)
    index === -1 ? ids.push(item.id) : ids.splice(index, 1)
    setExpand({ ids, idField: "id" })
    const node = clonedTree.find((node: TreeViewDataItem) => node.id === item.id)
    if (node) {
      node.expanded = !item?.expanded
    }

    dispatch(workspaceSlice.actions.setTree(clonedTree))
  }

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    handleExpandChange(event.item)
  }

  const onCheckChange = (event: TreeViewCheckChangeEvent) => {
    const settings: {
      singleMode: boolean
      checkChildren: boolean
      checkParents: boolean
    } = {
      singleMode: false,
      checkChildren: false,
      checkParents: false,
    }
    setCheck(handleTreeViewCheckChange(event, check, structuredClone(tree), settings))
  }

  useEffect(() => {
    const initialize = async () => {
      const workspace = workspaces.find(workspace => workspace.id === workspaceId)
      initialized.current = true
      initialWorkspaceUnit.current = workspace?.unit ?? 1
      await dispatch(getSideBarFiltersThunk({ tagAttributes, propertyCalculatorParameters }))
      dispatch(getCategory1TagAttributesValuesThunk(workspace?.unit ?? 1))
      dispatch(getProductionTagAttributesValuesThunk(workspace?.unit ?? 1))
      dispatch(getNumericTagAttributesValuesThunk(workspace?.unit ?? 1))
      await dispatch(getQuickPlotsThunk())
    }
    if (entityId !== "" && !initializedEntity.current) {
      initializedEntity.current = true
      if (entityId) {
        const tab = tabs.find((item) => item.id === entityId) || tabs.find((item) => item.isSelected) || tabs.find((item) => item.type === "mapView")
        tab && dispatch(workspaceSlice.actions.setSelectedTab(tab))
      } else {
        const tab = tabs.find((item) => item.isSelected) || tabs.find((item) => item.type === "mapView")
        tab && dispatch(workspaceSlice.actions.setSelectedTab(tab))
      }
    }

    if (tabs?.length && !initialized.current && workspaceId && workspaces?.length && tagAttributes?.length) {
      initialize()
    }
  }, [tabs?.length, workspaceId, workspaces, entityId, tagAttributes?.length])

  useEffect(() => {
    ;(async () => {
      await dispatch(getSideBarFiltersThunk({ tagAttributes, propertyCalculatorParameters }))
      await dispatch(getQuickPlotsThunk())
      const workspace = workspaces.find(workspace => workspace.id === workspaceId)
      if (workspace && initialWorkspaceUnit.current !== null && initialWorkspaceUnit.current !== workspace.unit) {
        dispatch(getNumericTagAttributesValuesThunk(workspace?.unit ?? 1))
        initialWorkspaceUnit.current = workspace.unit
      }
    })()
  }, [workspaceId])

  useEffect(() => {
    if (!tree.length) {
      dispatch(workspaceSlice.actions.setTree([...filtersTreeView]))
    }
  }, [tree])

  const { filterOptions, categorizeOptions } = filters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prevWorkspaceId = usePrevious(workspaceId)
  const prevFilterOptions = usePrevious(filterOptions)
  const prevCategorizeOptions = usePrevious(categorizeOptions)

  useEffect(() => {
    if (workspaceId && prevWorkspaceId === workspaceId) {
      const foundWorkspace = workspaces.find((item: IWorkspaceItem) => item.id === workspaceId)
      if (!foundWorkspace) return
      
      // Only trigger if filter options actually changed to prevent unnecessary API calls
      const filtersChanged = JSON.stringify(prevFilterOptions) !== JSON.stringify(filterOptions)
      const categorizeChanged = JSON.stringify(prevCategorizeOptions) !== JSON.stringify(categorizeOptions)
      
      if (!filtersChanged && !categorizeChanged) return
      
      ;(async () => {
        if(filterMode == 'explore'){
          // Filter-only changes (enable/disable, clear, apply toggles, filter edits) go
          // through the dedicated, read-only-safe FilterOptions endpoint. Categorize
          // changes still use the full workspace patch (which also carries filter state).
          if (categorizeChanged) {
            await dispatch(T_editWorkspaceFilters({ id: workspaceId, plotId, title: foundWorkspace?.title, unit: foundWorkspace?.unit, mapOptions: tree[2], filterOptions, categorizeOptions }))
          } else if (filtersChanged) {
            await dispatch(T_editWorkspaceFilterOptions({ id: workspaceId, filterOptions }))
          }
          // Rebuild tree and activeFiltersLegend to update legends across all plots
          await dispatch(getSideBarFiltersThunk({ tagAttributes, propertyCalculatorParameters }))
          isFetchedMap && (await dispatch(getMapViewFilteredPointsThunk({ workspaceId })))

          // A production plot is a table and a plot, and both may need updating on a filter
          // change. Neither is rebuilt by the refreshes above.
          //
          // To rectify this, we dispatch calls to refresh production data after the awaited save;
          // this allows the filter changes to have been saved to the database, meaning the
          // refreshed requests are answered according to the updated filters.
          if (selectedTab?.type === 6 && selectedTab.id) {
            const seriesIds = (productionPlotSeries ?? []).map((series) => series?.identifierFieldId).filter(Boolean)

            await dispatch(T_getProductionPlotData({ workspaceId, plotId: selectedTab.id }))
            await dispatch(T_getProductionPlotIndentifierOptions({ workspaceId, ids: seriesIds }))
          }
        }
      })()
    }
  }, [filterOptions, categorizeOptions, workspaceId, filterMode])

  useEffect(() => {
    if (!tagAttributes?.length) {
      dispatch(TH_GetAllTypes())
    }
  }, [tagAttributes])

  useEffect(() => {
    dispatch(T_getDatasetsList())
  }, [])

  // License initialization useEffect - runs once when sidebar mounts
  useEffect(() => {
    if (!licenseInfo?.datasets) return;
    
    const processLicenseChanges = async () => {
      const userDatasets = licenseInfo.datasets || [];
      
      const changes: Array<{element: IElementType, value: boolean, group: string}> = [];
      
      const elementRequirements = [
        { 
          datasets: ["subsurface_with_grid", "subsurface_no_grid"], 
          element: "subsurfaceAssets" 
        },
        { datasets: ["infrastructure"], element: "globalPipelineNetworks" },
        { datasets: ["emissions"], element: "cO2Emitters" },
        { datasets: ["ccs_projects"], element: "ccsProjects" },
        { datasets: ["blocks"], element: "blocks" },
        { datasets: ["licences"], element: "licences" },
        { datasets: ["windfarms"], element: "windfarms" }
      ];
      
      elementRequirements.forEach(({ datasets, element }) => {
        if (!datasets.some(ds => userDatasets.includes(ds))) {
          changes.push({
            element: element as IElementType, 
            value: false, 
            group: "serverSideOptions"
          });
        }
      });
      
      if (changes.length > 0) {
        changes.forEach(change => {
          dispatch(workspaceSlice.actions.setMapOptionItem(change));
        });
      }
      
      try {
        if(workspaceId !== null &&  workspaceId !== ''){
          // Persisting the license-derived defaults is a background write, so skip it
          // on read-only workspaces (it 500s server-side). Still fetch the map points
          // so read-only users can view the map.
          if (!isReadOnly) {
            await dispatch(saveMapOptionsThunk({id: workspaceId, mapOptions}));
          }
          mapEndpointSource.current = axios.CancelToken.source();
          await dispatch(getMapViewFilteredPointsThunk({ workspaceId, source: mapEndpointSource.current }));
        }
      } catch (error) {
        console.error("Error saving map options:", error);
      }
    };
    
    processLicenseChanges();
    
    return () => {
      requestDeduplicationService.clearAll();
    };
  }, [licenseInfo?.datasets, dispatch, workspaceId, mapOptions, isReadOnly]);

  useEffect(() => {
    return () => {
      requestDeduplicationService.clearAll()
    }
  }, [])

  const toggleSidebar = () => dispatch(sideBarTreeSlice.actions.setSidebarOpen(!isSidebarOpen))

  useEffect(() => {
    if (window.innerWidth < SCREEN_RESOLUTIONS.LAPTOP) {
      dispatch(sideBarTreeSlice.actions.setSidebarOpen(false))
    }
  }, [])

  const renderOptions = useMemo(() => {
    if (location.pathname.includes(ROUTES.explore)) {
      switch (selectedTab?.type) {
        case "mapView":
          return <MapOptions />
        case 1:
          return <PlotOptions />
        case 2:
          return <HistogramOptions />
        case 4:
          return <GridOptions />
        case 6:
          return <ProductionPlotOptions />
        case 5:
          return <ProjectViewOptions />
        case 7:
          return <BenchmarkOptions />
        default:
          return null
      }
    } else if (location.pathname.includes(ROUTES.dashboard) || location.pathname.includes(ROUTES.booking)) {
      return <MapOptions />
    }
  }, [selectedTab, location.pathname])

  const activeTree = useMemo(() => (filteredFilters.length ? filteredFilters : tree), [filteredFilters, tree])

  const handleToggleSidebar = useCallback(() => {
    toggleSidebar()
  }, [toggleSidebar])

  if (pathname === "/Admin") return null

  return (location.pathname.includes("analogue") || location.pathname.includes("home") || location.pathname.includes("data")) ? null : (
    <div className="sticky-area">
      <div className={combineClassNames(["side-bar", isSidebarOpen ? "opened" : "closed"])}>
        <button className="chevron-left-btn" onClick={handleToggleSidebar} />
        {
          // (isSidebarOpen && !hiddenSidebar) &&
          <div className="sidebarContent">
            {filteredFilters.length || tree.length ? (
              <>
                <div className="filters">
                  <div className="contentVisibilityHandler">
                    <h3 onClick={() => setFiltersOpened(!filtersOpened)} style={{ cursor: "pointer" }}>
                      <span className={filtersOpened ? "k-icon k-i-caret-alt-down" : "k-icon k-i-caret-alt-right"}></span>Filter Options
                    </h3>
                  </div>
                  <div className={filtersOpened ? "filterContent" : "filterContent closed"}>
                    <div className="filterOptions">
                      <FilterOptions searchValue={filtersSearchText} />
                    </div>
                    <TreeView
                      data={processTreeViewItems(activeTree[0].items, {
                        check: check,
                        expand: expand,
                      }).map((item) => ({
                        ...item,
                        handleExpandChange: () => handleExpandChange(item),
                      }))} // eslint-disable-line
                      expandIcons={true}
                      // className={`${tree[0].expanded ? 'filtersTree' : 'filtersTreeClosed'} ${filteredFilters.length ? 'filteredFilters' : ''}`}
                      onExpandChange={onExpandChange}
                      onCheckChange={onCheckChange}
                      item={TreeItem}
                    />
                  </div>
                </div>
                {renderOptions}
              </>
            ) : (
              ""
            )}
          </div>
        }
      </div>
    </div>
  )
}

export default React.memo(SideBar)
